import React, { useMemo, useRef, useState } from "react";
import { useConfirmDialog } from "../../../hooks/useConfirmDialog";
import { Col, Row } from "react-bootstrap";
import { useFormik } from "formik";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import SearchSelect from "../../common/textfield/SearchSelect";
import SelectFiled from "../../common/textfield/SelectFiled";
import TextInput from "../../common/textfield/TextInput";
import DateSelector from "../../common/textfield/DateSelector";
import moment from "moment";
import { Button } from "../../common/Button";
import { failed, success, warn } from "../../common/Toastify";
import { useOrgQuery } from "../../../hooks/ReactQueryHooks/useOrgQuery";
import { getReportDeliveryAudit } from "../../api/ReportDeliveries";
import { triggerReportDelivery } from "../../apps/api/AppointmentRequests";
import AuditListTable from "./AuditListTable";
import AuditListPagination from "./AuditListPagination";
import EncounterDetailModal from "./EncounterDetailModal";
import {
  ROWS_PER_PAGE,
  statusFilterOptions,
  deliveryTypeFilterOptions,
  mapAuditRowForTable,
  formatAuditTimestamp,
  truncateError,
} from "./Constants";
import "./report-delivery-audit.css";

function RetryConfirmDetails({
  orgId,
  encounterId,
  registryLabel,
  fileType,
  lastError,
  lastCompletedAt,
}) {
  const err = lastError || "";
  const truncated = truncateError(err, 200);
  return (
    <dl className="rda-retry-confirm">
      <div className="rda-retry-confirm__row">
        <dt>Organization</dt>
        <dd>{orgId || "—"}</dd>
      </div>
      <div className="rda-retry-confirm__row">
        <dt>Encounter</dt>
        <dd>{encounterId || "—"}</dd>
      </div>
      <div className="rda-retry-confirm__row">
        <dt>Registry</dt>
        <dd>{registryLabel || "all"}</dd>
      </div>
      <div className="rda-retry-confirm__row">
        <dt>File type</dt>
        <dd>{fileType || "all"}</dd>
      </div>
      <div className="rda-retry-confirm__row">
        <dt>Last error</dt>
        <dd className="rda-retry-confirm__error">
          {err ? (truncated !== err ? `${truncated} (truncated)` : err) : "—"}
        </dd>
      </div>
      <div className="rda-retry-confirm__row">
        <dt>Last attempt</dt>
        <dd>{formatAuditTimestamp(lastCompletedAt)}</dd>
      </div>
    </dl>
  );
}

const emptyFilters = {
  org: "All",
  status: "",
  deliveryType: "",
  encounterId: "",
  from: moment().subtract(2, "days"),
  to: moment(),
};

function getDefaultFilters() {
  return {
    ...emptyFilters,
    from: moment().subtract(2, "days"),
    to: moment(),
  };
}

const FILTER_DATE_FORMATS = ["MM/DD/YYYY", "MM-DD-YYYY"];

function parseFilterDate(dateVal) {
  if (!dateVal) return null;
  if (moment.isMoment(dateVal)) {
    return dateVal.isValid() ? dateVal : null;
  }
  const m = moment(dateVal, FILTER_DATE_FORMATS, true);
  if (m.isValid()) return m;
  const loose = moment(dateVal);
  return loose.isValid() ? loose : null;
}

function toFromIso(dateStr) {
  const m = parseFilterDate(dateStr);
  if (!m) return null;
  return m.clone().startOf("day").toISOString();
}

function toToIso(dateStr) {
  const m = parseFilterDate(dateStr);
  if (!m) return null;
  return m.clone().endOf("day").toISOString();
}

/** API body is { status, data: { items, nextToken } }; tolerate flat shapes too. */
function parseAuditListResponse(res) {
  const nested = res?.data;
  const payload =
    nested && typeof nested === "object" && !Array.isArray(nested) ? nested : res;
  return {
    items: Array.isArray(payload?.items)
      ? payload.items
      : Array.isArray(res?.items)
        ? res.items
        : [],
    nextToken: payload?.nextToken ?? res?.nextToken ?? null,
  };
}

function ReportDeliveryAudit() {
  const { getConfirmation } = useConfirmDialog();
  const [organizations, setOrganizations] = useState([]);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [auditList, setAuditList] = useState([]);
  const [detail, setDetail] = useState(null); // { orgId, encounterId }
  const [detailLoading, setDetailLoading] = useState(false);
  const [summaryByRegistry, setSummaryByRegistry] = useState([]);
  const [retrySubmitting, setRetrySubmitting] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [maxPageReached, setMaxPageReached] = useState(0);
  const pageTokensRef = useRef([null]);
  const pageCacheRef = useRef([]);
  const appliedFiltersRef = useRef(getDefaultFilters());
  const orgsLoadedRef = useRef(false);
  const userSearchRef = useRef(false);
  const detailRef = useRef(null);
  const retryInFlightRef = useRef(false);

  const orgNameById = useMemo(
    () =>
      Object.fromEntries(
        organizations.map((org) => [org.value, org.name])
      ),
    [organizations]
  );

  const filtersBusy = isLoading || isFetching;

  const formik = useFormik({
    initialValues: getDefaultFilters(),
  });

  const buildApiArgs = (pageIndex) => {
    const f = appliedFiltersRef.current;
    const args = { limit: ROWS_PER_PAGE };

    if (pageIndex > 0 && pageTokensRef.current[pageIndex]) {
      args.nextToken = pageTokensRef.current[pageIndex];
    }
    if (f.org && f.org !== "All") args.orgId = f.org;
    if (f.encounterId?.trim()) args.encounterId = f.encounterId.trim();
    if (f.status) args.status = f.status;
    if (f.deliveryType) args.deliveryType = f.deliveryType;
    // Main list is delivery-outcome only; RETRY events stay in encounter summary counts.
    args.recordType = "DELIVERY";
    if (f.fromIso) args.from = f.fromIso;
    else if (f.from) args.from = toFromIso(f.from);
    if (f.toIso) args.to = f.toIso;
    else if (f.to) args.to = toToIso(f.to);

    return args;
  };

  const applyPageResult = (pageIndex, items, nextToken) => {
    const token = nextToken ?? null;
    const previousToken = pageTokensRef.current[pageIndex + 1];
    // A refetch returning a different cursor means rows shifted underneath us,
    // so anything cached past this page no longer lines up.
    if (previousToken !== undefined && previousToken !== token) {
      pageTokensRef.current = pageTokensRef.current.slice(0, pageIndex + 1);
      pageCacheRef.current = pageCacheRef.current.slice(0, pageIndex + 1);
      setMaxPageReached(pageIndex);
    } else {
      setMaxPageReached((prev) => Math.max(prev, pageIndex));
    }
    pageTokensRef.current[pageIndex + 1] = token;
    pageCacheRef.current[pageIndex] = items;
  };

  const fetchAuditPage = async (
    pageIndex,
    { resetTokens = false, updateTable = true } = {}
  ) => {
    if (resetTokens) {
      pageTokensRef.current = [null];
      pageCacheRef.current = [];
      pageIndex = 0;
      setMaxPageReached(0);
    }

    if (updateTable) setIsLoading(true);

    try {
      const res = await getReportDeliveryAudit(buildApiArgs(pageIndex));
      const { items, nextToken } = parseAuditListResponse(res);

      applyPageResult(pageIndex, items, nextToken);

      if (updateTable) {
        setAuditList(items.map((row) => mapAuditRowForTable(row, orgNameById)));
        setPage(pageIndex);
      }

      return { items, nextToken };
    } catch (err) {
      failed(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err.message
      );
      throw err;
    } finally {
      if (updateTable) {
        setIsLoading(false);
        setIsFetching(false);
      }
    }
  };

  const closeDetail = () => {
    detailRef.current = null;
    setDetail(null);
    setSummaryByRegistry([]);
  };

  const loadEncounterDetail = async ({ orgId, encounterId }) => {
    if (!orgId || !encounterId) {
      failed("Organization and encounter id are required for detail view");
      return;
    }
    if (
      detailLoading &&
      detailRef.current?.orgId === orgId &&
      detailRef.current?.encounterId === encounterId
    ) {
      return;
    }
    const selection = { orgId, encounterId };
    detailRef.current = selection;
    setDetail(selection);
    setDetailLoading(true);
    try {
      // Encounter mode returns summary.byRegistry (built from full history).
      const res = await getReportDeliveryAudit({
        orgId,
        encounterId,
        limit: 1,
      });
      // Ignore stale responses if user clicked another encounter.
      if (
        detailRef.current?.orgId !== orgId ||
        detailRef.current?.encounterId !== encounterId
      ) {
        return;
      }
      const payload = res?.data ?? {};
      setSummaryByRegistry(
        Array.isArray(payload.summary?.byRegistry)
          ? payload.summary.byRegistry
          : []
      );
    } catch (err) {
      failed(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err.message
      );
    } finally {
      if (
        detailRef.current?.orgId === orgId &&
        detailRef.current?.encounterId === encounterId
      ) {
        setDetailLoading(false);
      }
    }
  };

  const runSearch = async (filters) => {
    if (filters.from && filters.to) {
      const fromM = parseFilterDate(filters.from);
      const toM = parseFilterDate(filters.to);
      if (fromM && toM && toM.isBefore(fromM, "day")) {
        failed("From date must be on or before To date");
        return;
      }
    }
    // Backend requires orgId when encounterId is provided (encounter detail mode).
    if (filters.encounterId?.trim() && (!filters.org || filters.org === "All")) {
      failed("Select an organization when filtering by encounter id");
      return;
    }
    closeDetail();
    appliedFiltersRef.current = {
      ...filters,
      fromIso: toFromIso(filters.from),
      toIso: toToIso(filters.to),
    };
    try {
      await fetchAuditPage(0, { resetTokens: true });
    } finally {
      setHasSearched(true);
    }
  };

  useOrgQuery({
    onSuccess: (data) => {
      setOrganizations(
        data?.map((org) => ({ name: org?.name, value: org?.id })) || []
      );
      if (!orgsLoadedRef.current) {
        orgsLoadedRef.current = true;
        if (!userSearchRef.current) {
          runSearch(getDefaultFilters());
        }
      }
    },
    refetchOnWindowFocus: false,
  });

  const handlePageChange = async (newPage) => {
    if (filtersBusy) return;
    if (newPage < 0) return;
    if (newPage === page) return;

    const cachedItems = pageCacheRef.current[newPage];
    if (cachedItems) {
      setAuditList(
        cachedItems.map((row) => mapAuditRowForTable(row, orgNameById))
      );
      setPage(newPage);
      return;
    }

    // Page 0 needs no token; page N needs the token saved from page N-1.
    if (newPage > 0 && !pageTokensRef.current[newPage]) return;

    setIsFetching(true);
    try {
      await fetchAuditPage(newPage);
    } finally {
      setIsFetching(false);
    }
  };

  const handleSearch = () => {
    userSearchRef.current = true;
    runSearch({ ...formik.values });
  };

  const handleReset = () => {
    const defaults = getDefaultFilters();
    formik.setValues(defaults);
    runSearch(defaults);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    handleSearch();
  };

  const refreshAfterRetry = async () => {
    const currentDetail = detailRef.current;
    if (currentDetail?.orgId && currentDetail?.encounterId) {
      await loadEncounterDetail(currentDetail);
    }
    await fetchAuditPage(page);
  };

  const confirmAndRetry = async ({
    registryId,
    registryLabel,
    fileType,
    lastError,
    lastCompletedAt,
  }) => {
    const currentDetail = detailRef.current;
    if (!currentDetail?.encounterId || retrySubmitting || retryInFlightRef.current) {
      return;
    }

    const confirmed = await getConfirmation({
      title: "Retry file delivery",
      message: `Start a manual retry for this registry ${fileType || "file"} only? Other file types on this registry will not be sent.`,
      actionBtnTitle: "Retry",
      cancelBtnTitle: "Cancel",
      maxWidth: "sm",
      content: (
        <RetryConfirmDetails
          orgId={orgNameById[currentDetail.orgId] || currentDetail.orgId}
          encounterId={currentDetail.encounterId}
          registryLabel={registryLabel}
          fileType={fileType}
          lastError={lastError}
          lastCompletedAt={lastCompletedAt}
        />
      ),
    });

    if (!confirmed) return;

    retryInFlightRef.current = true;
    setRetrySubmitting(true);
    try {
      await triggerReportDelivery({
        encounterId: currentDetail.encounterId,
        ...(registryId ? { registryId } : {}),
        ...(registryId && fileType ? { fileType } : {}),
      });
      success("Report delivery retry started");
      await refreshAfterRetry();
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message;
      if (status === 429) {
        warn(
          msg ||
            "A retry was requested within the last 5 minutes. Please wait before trying again."
        );
      } else {
        failed(msg);
      }
    } finally {
      retryInFlightRef.current = false;
      setRetrySubmitting(false);
    }
  };

  const handleRetryRegistry = (row) => {
    confirmAndRetry({
      registryId: row.registryId,
      registryLabel: row.registryName || row.registryId || "—",
      fileType: row.fileType,
      lastError: row.lastError,
      lastCompletedAt: row.lastCompletedAt,
    });
  };

  const orgSelectOptions = [
    { label: "All", value: "All" },
    ...organizations.map((o) => ({ label: o.name, value: o.value })),
  ];

  // Pages already visited stay clickable; one more is offered while a cursor exists.
  const hasMorePages = Boolean(pageTokensRef.current[maxPageReached + 1]);
  const knownRowCount = pageCacheRef.current.reduce(
    (total, rows) => total + (rows?.length || 0),
    0
  );
  const pageCount = Math.max(1, maxPageReached + 1 + (hasMorePages ? 1 : 0));

  return (
    <section className="common-listing rda-page">
      <h1 className="rda-page__title">Report Delivery Audit</h1>

      <form
        className="common-form border-fields encounterSearch rda-filters"
        onSubmit={handleSearchSubmit}
      >
        <Row>
          <Col md={2}>
            <SearchSelect
              options={orgSelectOptions}
              value={
                orgSelectOptions.find((o) => o.value === formik.values.org) ||
                null
              }
              onChange={(selected) =>
                formik.setFieldValue("org", selected?.value || "All")
              }
              placeholder="Select Organization"
              isDisabled={filtersBusy}
              formik={formik}
              keyField="org"
              hideRequired={true}
              isClearable={false}
              label="Organization"
            />
          </Col>
          <Col md={2}>
            <TextInput
              keyField="encounterId"
              formik={formik}
              label="Encounter Id"
              placeholder="Encounter Id"
              hideRequired={true}
              disabled={filtersBusy}
            />
          </Col>
          <Col md={2}>
            <SelectFiled
              keyField="status"
              formik={formik}
              options={statusFilterOptions}
              selectLabel="All statuses"
              label="Status"
              required={false}
              optional={false}
              selectDisabledValue={true}
              readOnly={filtersBusy}
            />
          </Col>
          <Col md={2}>
            <SelectFiled
              keyField="deliveryType"
              formik={formik}
              options={deliveryTypeFilterOptions}
              selectLabel="All delivery types"
              label="Delivery type"
              required={false}
              optional={false}
              selectDisabledValue={true}
              readOnly={filtersBusy}
            />
          </Col>
        </Row>
        <Row className="mt-2">
          <Col md={2} sm={6} xs={12}>
            <DateSelector
              keyField="from"
              label="From"
              formik={formik}
              hasValue={moment()}
              minDate=""
              maxDate={moment(formik.values.to)}
              hideRequired
              required={false}
              readOnly={filtersBusy}
            />
          </Col>
          <Col md={2} sm={6} xs={12}>
            <DateSelector
              keyField="to"
              label="To"
              formik={formik}
              minDate={moment(formik.values.from)}
              maxDate={moment()}
              hasValue={moment()}
              hideRequired
              required={false}
              readOnly={filtersBusy}
            />
          </Col>
        </Row>
        <div className="rda-actions">
          <Button type="submit" isLoading={isLoading} disabled={filtersBusy}>
            <SearchOutlinedIcon /> Search
          </Button>
          <Button
            type="button"
            onClick={handleReset}
            variant="secondary"
            title="Clear"
            disabled={filtersBusy}
          >
            <ClearIcon /> Clear
          </Button>
        </div>
      </form>

      <AuditListTable
        rows={auditList}
        loading={isLoading}
        fetching={isFetching}
        emptyMessage={
          hasSearched
            ? "No delivery records match your filters."
            : "No Data Available"
        }
        onRowSelect={loadEncounterDetail}
      />
      {auditList?.length > 0 ? (
        <AuditListPagination
          page={page}
          rowsPerPage={ROWS_PER_PAGE}
          rowCount={auditList.length}
          pageCount={pageCount}
          knownRowCount={knownRowCount}
          hasMorePages={hasMorePages}
          onPageChange={handlePageChange}
        />
      ) : null}

      <EncounterDetailModal
        show={Boolean(detail)}
        orgId={detail?.orgId}
        orgName={orgNameById[detail?.orgId]}
        encounterId={detail?.encounterId}
        summaryByRegistry={summaryByRegistry}
        loading={detailLoading}
        retrySubmitting={retrySubmitting}
        onClose={closeDetail}
        onRetryRegistry={handleRetryRegistry}
      />
    </section>
  );
}

export default ReportDeliveryAudit;
